import { CircularProgress, InputAdornment } from '@mui/material';
import { useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { SelectElement, SelectElementProps, useWatch } from 'react-hook-form-mui';

type SelectOption = {
  id: string | number;
  label: string | React.ReactNode;
};

type SelectElementWithLoadingProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue extends SelectOption = SelectOption,
> = Omit<SelectElementProps<TFieldValues, TName, TValue>, 'options' | 'InputProps'> & {
  /**
   * Whether the options are currently loading
   */
  isLoading?: boolean;
  /**
   * The options array. Each option should have an `id` and `label` property.
   */
  options: TValue[];
  /**
   * Additional InputProps to merge with the loading indicator
   */
  InputProps?: SelectElementProps<TFieldValues, TName, TValue>['InputProps'];
};

/**
 * A wrapper around SelectElement that handles the case where options are loading
 * and the current form value might not be in the options yet.
 *
 * When options are loading and the current value doesn't exist in the options,
 * it automatically adds a dummy option with the current value to prevent MUI errors.
 * It also automatically adds a loading indicator when isLoading is true.
 */
export function SelectElementWithLoading<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue extends SelectOption = SelectOption,
>({
  name,
  options,
  isLoading = false,
  valueKey = 'id',
  labelKey = 'label',
  InputProps,
  ...rest
}: SelectElementWithLoadingProps<TFieldValues, TName, TValue>) {
  const currentValue = useWatch({ name });

  const enhancedOptions = useMemo(() => {
    // If options are loading and we have a value that's not in the options,
    // add a dummy option to prevent MUI error
    if (
      isLoading &&
      currentValue &&
      currentValue !== '' &&
      !options.some((opt) => {
        const optValue = valueKey ? (opt as any)[valueKey] : opt;
        return String(optValue) === String(currentValue);
      })
    ) {
      return [
        ...options,
        {
          [valueKey]: currentValue,
          [labelKey]: '',
        } as TValue,
      ];
    }

    return options;
  }, [options, isLoading, currentValue, valueKey, labelKey]);

  const enhancedInputProps = useMemo(() => {
    const loadingAdornment = isLoading ? (
      <InputAdornment position="start" sx={{ zIndex: 1 }}>
        <CircularProgress size={20} />
      </InputAdornment>
    ) : undefined;

    if (!InputProps && !loadingAdornment) {
      return undefined;
    }

    return {
      ...InputProps,
      startAdornment: loadingAdornment || InputProps?.startAdornment,
    };
  }, [isLoading, InputProps]);

  return (
    <SelectElement
      name={name}
      options={enhancedOptions}
      valueKey={valueKey}
      labelKey={labelKey}
      disabled={isLoading || rest.disabled}
      InputProps={enhancedInputProps}
      {...rest}
    />
  );
}
